import React from 'react';
import './index.css';

/**
 * H2: 骨架屏组件
 *
 * 页面加载时显示占位骨架，提升感知性能。
 *
 * 用法：
 * <Skeleton type="card" count={4} />  // 4个卡片骨架
 * <Skeleton type="text" lines={3} />   // 3行文本骨架
 * <Skeleton type="avatar" />           // 头像骨架
 */

/**
 * 骨架屏元素（灰色条块 + shimmer动画）
 */
function SkeletonBlock({ width, height, borderRadius, style }) {
    return (
        <div
            className="skeleton-block"
            style={{
                width: width || '100%',
                height: height || '16px',
                borderRadius: borderRadius || '4px',
                ...style
            }}
        />
    );
}

/**
 * 骨架屏组件
 *
 * @param {Object} props
 * @param {string} props.type - 类型: 'card' | 'text' | 'avatar' | 'custom'
 * @param {number} props.count - 数量（card类型时表示卡片数）
 * @param {number} props.lines - 行数（text类型时）
 * @param {Object} props.style - 容器样式
 */
export default function Skeleton({ type = 'text', count = 1, lines = 3, style }) {
    const renderSkeleton = () => {
        switch (type) {
            case 'card':
                // 首页卡片骨架：模拟多个功能卡片
                return Array.from({ length: count }, (_, i) => (
                    <div key={i} className="skeleton-card">
                        <SkeletonBlock
                            width="100%"
                            height="80px"
                            borderRadius="10px"
                            style={{ marginBottom: '12px' }}
                        />
                    </div>
                ));

            case 'avatar':
                // 头像 + 文字骨架
                return (
                    <div className="skeleton-avatar-row">
                        <SkeletonBlock
                            width="48px"
                            height="48px"
                            borderRadius="50%"
                        />
                        <div className="skeleton-avatar-text">
                            <SkeletonBlock
                                width="120px"
                                height="14px"
                                style={{ marginBottom: '8px' }}
                            />
                            <SkeletonBlock
                                width="80px"
                                height="12px"
                            />
                        </div>
                    </div>
                );

            case 'text':
                // 多行文本骨架
                return Array.from({ length: lines }, (_, i) => (
                    <SkeletonBlock
                        key={i}
                        width={i === lines - 1 ? '60%' : '100%'}
                        height="14px"
                        style={{ marginBottom: '10px' }}
                    />
                ));

            case 'custom':
            default:
                // 自定义：子元素直接传入
                return <div className="skeleton-custom">{count > 0 ? Array.from({ length: count }, (_, i) => (
                    <SkeletonBlock key={i} height="60px" style={{ marginBottom: '12px' }} />
                )) : null}</div>;
        }
    };

    return (
        <div className="skeleton-container" style={style}>
            {renderSkeleton()}
        </div>
    );
}
